import { SignatureV4 } from "@aws-sdk/signature-v4";
import { Sha256 } from "@aws-crypto/sha256-js";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import { CognitoIdentityClient } from "@aws-sdk/client-cognito-identity";

/**
 * API呼び出しのためのユーティリティ関数
 * 認証ヘッダーを自動的に追加するfetchラッパー
 */
import { fetchAuthSession } from "aws-amplify/auth";

/**
 * Cognitoから現在のIDトークンを取得する
 * @returns IDトークン文字列
 */
export const getIdToken = async (): Promise<string> => {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || "";
  } catch (error) {
    console.error("ID Token取得エラー:", error);
    throw new Error("認証情報の取得に失敗しました。再ログインしてください。");
  }
};

/**
 * API呼び出しの基本設定を行う関数
 * @param options fetchオプション
 * @returns 認証ヘッダーを追加したfetchオプション
 */
export const createApiOptions = async (
  options: RequestInit = {},
): Promise<RequestInit> => {
  const idToken = await getIdToken();

  return {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${idToken}`,
    },
  };
};

/**
 * GET APIリクエスト
 * @param url エンドポイントURL
 * @param options fetchオプション（オプショナル）
 * @returns レスポンスのJSONデータ
 */
export const apiGet = async <T = unknown>(
  url: string,
  options: RequestInit = {},
): Promise<T> => {
  const apiOptions = await createApiOptions(options);

  const response = await fetch(url, apiOptions);

  if (!response.ok) {
    await handleApiError(response);
  }

  return response.json() as Promise<T>;
};

/**
 * POST APIリクエスト（JSON）
 * @param url エンドポイントURL
 * @param data 送信するデータ
 * @param options fetchオプション（オプショナル）
 * @returns レスポンスのJSONデータ
 */
export const apiPost = async <T = unknown, D = unknown>(
  url: string,
  data?: D,
  options: RequestInit = {},
): Promise<T> => {
  const apiOptions = await createApiOptions({
    ...options,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    body: JSON.stringify(data || {}),
  });

  const response = await fetch(url, apiOptions);

  if (!response.ok) {
    await handleApiError(response);
  }

  return response.json() as Promise<T>;
};

/**
 * PATCH APIリクエスト（JSON）
 * @param url エンドポイントURL
 * @param data 送信するデータ
 * @param options fetchオプション（オプショナル）
 * @returns レスポンスのJSONデータ
 */
export const apiPatch = async <T = unknown, D = unknown>(
  url: string,
  data?: D,
  options: RequestInit = {},
): Promise<T> => {
  const apiOptions = await createApiOptions({
    ...options,
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    body: JSON.stringify(data || {}),
  });

  const response = await fetch(url, apiOptions);

  if (!response.ok) {
    await handleApiError(response);
  }

  return response.json() as Promise<T>;
};

/**
 * DELETE APIリクエスト（JSON）
 * @param url エンドポイントURL
 * @param data 送信するデータ（オプショナル）
 * @param options fetchオプション（オプショナル）
 * @returns レスポンスのJSONデータ
 */
export const apiDelete = async <T = unknown, D = unknown>(
  url: string,
  data?: D,
  options: RequestInit = {},
): Promise<T> => {
  const apiOptions = await createApiOptions({
    ...options,
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    body: data ? JSON.stringify(data) : undefined,
  });

  const response = await fetch(url, apiOptions);

  if (!response.ok) {
    await handleApiError(response);
  }

  return response.json() as Promise<T>;
};

/**
 * PUT APIリクエスト（任意のボディ形式に対応）
 * @template T レスポンス型
 * @param url エンドポイントURL
 * @param body 送信するデータ（File, Blob, JSON, FormDataなど）
 * @param options fetchオプション（オプショナル）
 * @returns パースされたJSONレスポンス（T型）
 */
export const apiPut = async <T = unknown>(
  url: string,
  body: BodyInit,
  options: RequestInit = {},
): Promise<T | void> => {
  const apiOptions: RequestInit = {
    ...options,
    method: "PUT",
    body,
    headers: {
      ...(options.headers || {}),
    },
  };

  const response = await fetch(url, apiOptions);

  console.log({ response });

  if (!response.ok) {
    await handleApiError(response);
  }

  const contentLength = response.headers.get("Content-Length");
  const contentType = response.headers.get("Content-Type");

  // JSON が返ってくると期待される場合のみパース
  if (contentLength !== "0" && contentType?.includes("application/json")) {
    try {
      return (await response.json()) as T;
    } catch {
      console.warn("レスポンスのJSONパースに失敗しましたが、無視します");
      return undefined;
    }
  }

  // ボディが空なら void を返す（問題なし）
  return undefined;
};

/**
 * POST APIリクエスト（FormData）
 * @param url エンドポイントURL
 * @param formData 送信するFormData
 * @param options fetchオプション（オプショナル）
 * @returns レスポンスのJSONデータ
 */
export const apiPostFormData = async <T = unknown>(
  url: string,
  formData: FormData,
  options: RequestInit = {},
): Promise<T> => {
  // FormDataを使用する場合は、Content-Typeヘッダーを自動的に設定するため、
  // 明示的に設定しないことで、ブラウザが適切なContent-Typeとboundaryを設定する
  const apiOptions = await createApiOptions({
    ...options,
    method: "POST",
    body: formData,
  });

  const response = await fetch(url, apiOptions);

  if (!response.ok) {
    await handleApiError(response);
  }

  return response.json() as Promise<T>;
};

/**
 * POST APIリクエスト（Blob形式のレスポンス）
 * @param url エンドポイントURL
 * @param data 送信するデータ
 * @param options fetchオプション（オプショナル）
 * @returns Blobデータとヘッダー情報
 */
export const apiPostBlob = async <D = unknown>(
  url: string,
  data: D,
  options: RequestInit = {},
): Promise<{ blob: Blob; headers: Headers }> => {
  const apiOptions = await createApiOptions({
    ...options,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    body: JSON.stringify(data),
  });

  const response = await fetch(url, apiOptions);

  if (!response.ok) {
    await handleApiError(response);
  }

  const blob = await response.blob();
  return { blob, headers: response.headers };
};

/**
 * APIエラー処理
 * @param response fetchレスポンス
 * @throws エラーメッセージ
 */
export const handleApiError = async (response: Response): Promise<never> => {
  // 403の場合は即座にリダイレクト（ループ防止のため403ページ上では実行しない）
  if (response.status === 403 && !window.location.pathname.includes("/403")) {
    const basePath = import.meta.env.BASE_URL || "/";
    const redirectUrl = basePath.endsWith("/")
      ? `${basePath}403`
      : `${basePath}/403`;
    console.error(
      `403 Forbidden on ${response.url}: リダイレクトします`,
      redirectUrl,
    );
    window.location.href = redirectUrl;
    throw new Error(`Forbidden: Redirecting to ${redirectUrl}`);
  }

  let errorMessage = `サーバーエラー: ${response.status}`;

  try {
    const contentType = response.headers.get("content-type");

    if (contentType && contentType.includes("application/json")) {
      const errorData = await response.json();
      errorMessage = errorData.error || errorData.message || errorMessage;
    } else {
      const errorText = await response.text();
      if (errorText) {
        errorMessage = `${errorMessage} - ${errorText.substring(0, 100)}`;
      }
    }
  } catch (e) {
    console.error("エラーレスポンスの解析に失敗:", e);
  }

  throw new Error(errorMessage);
};

/*
 * Lambda関数URL (POST) を受け取り、Idpoolを利用した署名付きURLを作成して実行する関数
 * 関数URLにCognito認証を付与する際に使用する
 */
export const apiSigV4URL = async <T = unknown, D = unknown>(
  url: string,
  data: D,
  options: RequestInit = {},
  awsSettings: {
    region?: string;
    userPoolId: string;
    idPoolId: string;
  },
): Promise<T> => {
  console.log({ url, data, options, awsSettings });

  // Cognitoクレデンシャルプロバイダーを設定
  const region = awsSettings.region || "ap-northeast-1";
  const identityProviderKey = `cognito-idp.ap-northeast-1.amazonaws.com/${awsSettings.userPoolId}`;
  const credentialsProvider = fromCognitoIdentityPool({
    client: new CognitoIdentityClient({ region }),
    identityPoolId: awsSettings.idPoolId,
    logins: {
      [identityProviderKey]: async () => await getIdToken(),
    },
  });

  // SigV4 署名インスタンスを作成
  const sigV4 = new SignatureV4({
    service: "lambda",
    region,
    credentials: credentialsProvider,
    sha256: Sha256,
  });

  // SigV4署名付きリクエストを作成
  const urlObj = new URL(url);
  const body = JSON.stringify(data);

  const request = {
    method: "POST",
    hostname: urlObj.hostname,
    path: urlObj.pathname + url.search,
    protocol: urlObj.protocol,
    headers: {
      "Content-Type": "application/json",
      host: urlObj.hostname,
    },
    body,
  };
  // console.log({ request });

  // リクエストに署名を追加
  const signedRequest = await sigV4.sign(request);
  // console.log({ signedRequest });

  // 署名付きヘッダーでfetchを実行
  const response = await fetch(url, {
    ...options,
    method: "POST",
    headers: {
      ...options.headers,
      ...signedRequest.headers,
    },
    body,
  });
  // console.log({ response });

  return response.json() as Promise<T>;
};
